<script setup>
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import dateFormat from "@/shared/lib/date-format";
import BottomSheet from "@/shared/ui/bottom-sheet/BottomSheet.vue";
import ParticipantCard from "@/pages/admin/client_cabinet/components/shared/ParticipantCard.vue";
import RelatedActionsList from "@/pages/admin/client_cabinet/components/shared/RelatedActionsList.vue";
import { getStatusLabel } from "@/shared/config/statusDictionary";
import StatusDot from "@/shared/ui/status-dot/StatusDot.vue";
import { useParticipantTypes } from "@/entities/directory";

const { t } = useI18n();

const props = defineProps({
  form: {
    type: Object,
    required: true,
  },
  templateLabel: {
    type: String,
    default: "",
  },
  participants: {
    type: Array,
    default: () => [],
  },
  links: {
    type: Array,
    default: () => [],
  },
});

const showLinksDrawer = ref(false);
const showParticipantsDrawer = ref(false);

const { participantTypes: participant_types, fetchParticipantTypes } =
  useParticipantTypes();

const statusKey = computed(() => {
  return (
    props.form?.status?.code ||
    props.form?.status?.value ||
    props.form?.status ||
    ""
  );
});

const statusLabel = computed(() => {
  if (!statusKey.value) return t("Не указано");
  return (
    getStatusLabel(statusKey.value) ||
    props.form?.status?.label ||
    props.form?.status?.name ||
    statusKey.value
  );
});

function formatDate(date) {
  if (!date) return t("Не указано");
  return dateFormat.timestamp_to_local_tz(date, "DD.MM.YYYY HH:mm");
}

onMounted(() => {
  fetchParticipantTypes();
});
</script>

<template>
  <div class="header-card">
    <div class="top">
      <div class="number">№ {{ form?.number || form?.uuid || form?.id }}</div>
      <div class="status">
        <StatusDot :code="statusKey" />
        <div class="status-text">{{ statusLabel }}</div>
      </div>
    </div>

    <div class="date">
      {{ formatDate(form?.registration_date || form?.created_at) }}
    </div>

    <div class="meta">
      <div class="row">
        <svg-icon name="huge-document-info" />
        <div class="text">
          {{
            templateLabel ||
            form?.template?.name ||
            form?.template_name ||
            form?.template_code ||
            $t("Не указано")
          }}
        </div>
      </div>
      <div class="row">
        <svg-icon name="huge-user" />
        <div class="text flex gap-1.5">
          <div>{{ $t("Нотариус:") }}</div>

          <div class="font-normal">{{ form?.notary_info.fio }}</div>
        </div>
      </div>
    </div>

    <div class="links">
      <button class="link-row" type="button" @click="showLinksDrawer = true">
        <svg-icon name="huge-document-link" class="icon" />
        <div class="text">{{ $t("Связанные действия") }}</div>
        <div class="count">{{ links.length }}</div>
        <svg-icon name="huge-chevron-right" class="chevron" />
      </button>
      <button
        class="link-row"
        type="button"
        @click="showParticipantsDrawer = true"
      >
        <svg-icon name="huge-users" class="icon" />
        <div class="text">{{ $t("Участники") }}</div>
        <div class="count">{{ participants.length }}</div>
        <svg-icon name="huge-chevron-right" class="chevron" />
      </button>
    </div>
  </div>

  <BottomSheet
    v-model="showLinksDrawer"
    :title="$t('Связанные действия')"
    height="auto"
    width="auto"
  >
    <RelatedActionsList :links="links" :with-border="true" />
  </BottomSheet>

  <BottomSheet
    v-model="showParticipantsDrawer"
    :title="$t('Участники')"
    height="auto"
    width="auto"
  >
    <div v-if="!participants.length" class="drawer-empty">
      {{ $t("Участники отсутствуют") }}
    </div>
    <div v-else class="drawer-list">
      <ParticipantCard
        v-if="participants?.length"
        v-for="p in participants"
        :key="p.iin"
        :participant="p"
        :participant-types="participant_types"
        class="flex gap-4 border border-solid border-border p-4 rounded-2xl mb-4"
      />
    </div>
  </BottomSheet>
</template>

<style scoped lang="scss">
.header-card {
  @apply w-full p-5 bg-card rounded-[20px] outline outline-1 outline-offset-[-1px];
  @apply outline-border flex flex-col gap-3;

  .top {
    @apply w-full flex items-center justify-between;
  }

  .number {
    @apply text-base font-semibold text-foreground leading-6;
  }

  .status {
    @apply px-2 py-1 rounded-md outline outline-1 outline-offset-[-1px];
    @apply outline-border bg-muted flex items-center gap-1.5;
  }

  .status-text {
    @apply text-sm text-foreground leading-5;
  }

  .date {
    @apply text-sm font-normal text-muted-foreground leading-5;
  }

  .meta {
    @apply flex flex-col gap-4;
  }

  .row {
    @apply flex items-center gap-2.5;

    svg {
      @apply w-4 h-4 text-muted-foreground;
    }
  }

  .icon {
    @apply w-5 h-5 text-muted-foreground;
  }

  .text {
    @apply text-foreground text-sm leading-5;
  }

  .links {
    @apply flex flex-col gap-4 border-t border-border pt-3;
  }

  .link-row {
    @apply w-full flex items-center gap-2 text-foreground;

    svg {
      @apply w-5 h-5 text-muted-foreground;
    }
  }

  .count {
    @apply ml-auto text-sm text-muted-foreground;
  }

  .chevron {
    @apply w-4 h-4 text-muted-foreground;
  }

  .drawer-list {
    @apply flex flex-col gap-3;
  }

  .drawer-item {
    @apply w-full p-4 bg-card rounded-2xl outline outline-1 outline-offset-[-1px];
    @apply outline-border flex items-center gap-3;
  }

  .item-icon {
    @apply w-5 h-5 text-muted-foreground;
  }

  .item-text {
    @apply flex flex-col gap-1;
  }

  .item-title {
    @apply text-sm font-semibold text-foreground;
  }

  .item-subtitle {
    @apply text-xs text-muted-foreground;
  }
}
</style>
